import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAutosizeOptions {
  maxHeight: string;
  onOverflowChange?: (isOverflowing: boolean) => void;
}

interface UseAutosizeResult {
  contentRef: React.RefObject<HTMLDivElement | null>;
  isOverflowing: boolean;
}

const parseToNumeric = (value: string, rootFontSize: number): number => {
  const numericValue = parseFloat(value);
  if (value.endsWith('rem')) {
    return numericValue * rootFontSize;
  }
  return numericValue;
};

export const useAutosize = ({ maxHeight, onOverflowChange }: UseAutosizeOptions): UseAutosizeResult => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const prevOverflowingRef = useRef(false);
  const onOverflowChangeRef = useRef(onOverflowChange);
  onOverflowChangeRef.current = onOverflowChange;

  const handleOverflowChange = useCallback((overflowing: boolean) => {
    if (overflowing !== prevOverflowingRef.current) {
      prevOverflowingRef.current = overflowing;
      setIsOverflowing(overflowing);
      onOverflowChangeRef.current?.(overflowing);
    }
  }, []);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const maxHeightResolved = parseToNumeric(maxHeight, rootFontSize);

    const observer = new ResizeObserver(([entry]) => {
      handleOverflowChange(entry.contentRect.height > maxHeightResolved);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [maxHeight, handleOverflowChange]);

  return { contentRef, isOverflowing };
};
